import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { Box, BoxProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { CustomTabsProps, Tabs } from './Tabs';

const DEFAULT_GRADIENT_BACKGROUND = 'linear-gradient(180deg, #FAF9F5, rgba(250, 249, 245, 0.00))';

type StickyTabsProps = Omit<CustomTabsProps, 'content'> & {
    content: React.ReactNode;
    showGradient?: boolean;
    topSpacing?: number;
    gradientFadeDistance?: number;
    gradientBackground?: string;
    stickyBoxProps?: BoxProps;
};

export const StickyTabs = ({
    showGradient = true,
    topSpacing = 20,
    gradientFadeDistance = 100,
    gradientBackground = DEFAULT_GRADIENT_BACKGROUND,
    stickyBoxProps,
    children,
    content,
    ...tabsProps
}: StickyTabsProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollElement = useMaxWidthWrapperScrollElement();
    const [gradientOpacity, setGradientOpacity] = useState(0);

    useEffect(() => {
        if (!showGradient || !scrollElement || !containerRef.current) {
            return;
        }

        const updateGradientOpacity = () => {
            if (!containerRef.current) {
                return;
            }

            const rootTop = scrollElement.getBoundingClientRect().top;
            const containerTop = containerRef.current.getBoundingClientRect().top;
            const stickyScrollDistance = Math.max(0, rootTop - containerTop - topSpacing);
            const nextOpacity = Math.min(stickyScrollDistance / gradientFadeDistance, 1);

            setGradientOpacity((currentOpacity) =>
                Math.abs(currentOpacity - nextOpacity) < 0.01 ? currentOpacity : nextOpacity
            );
        };

        updateGradientOpacity();
        scrollElement.addEventListener('scroll', updateGradientOpacity, { passive: true });
        window.addEventListener('resize', updateGradientOpacity);

        return () => {
            scrollElement.removeEventListener('scroll', updateGradientOpacity);
            window.removeEventListener('resize', updateGradientOpacity);
        };
    }, [gradientFadeDistance, scrollElement, showGradient, topSpacing]);

    return (
        <Box ref={containerRef} pt={`${topSpacing}px`}>
            <Box
                position="sticky"
                top={0}
                zIndex={2}
                bgcolor="secondary-alt.light"
                pt="10px"
                px={{ xs: 2, md: 4 }}
                {...stickyBoxProps}
            >
                <Tabs {...tabsProps}>{children}</Tabs>
                {showGradient ? (
                    <Box
                        aria-hidden
                        position="absolute"
                        left={0}
                        top="100%"
                        width="100%"
                        height="75px"
                        sx={{
                            pointerEvents: 'none',
                            opacity: gradientOpacity,
                            background: gradientBackground,
                        }}
                    />
                ) : null}
            </Box>
            {content}
        </Box>
    );
};
